/**
 * A confirmation an async flow can wait for: `await confirm({...})` opens the
 * app's ConfirmModal and resolves true (confirm) or false (cancel / Escape).
 * Render `modal` once, anywhere in the component.
 *
 * Used where a save must be confirmed before it is sent — "Saving makes this
 * connector customized for this agent" — without forking the forms that save.
 */

import { useCallback, useRef, useState, type ReactNode } from 'react';
import { ConfirmModal } from '@/components/common/ConfirmModal';

export interface ConfirmStepOptions {
  title: string;
  message: string;
  confirmText: string;
  cancelText: string;
  variant?: 'danger' | 'warning' | 'info';
}

export function useConfirmStep(): { confirm: (options: ConfirmStepOptions) => Promise<boolean>; modal: ReactNode } {
  const [options, setOptions] = useState<ConfirmStepOptions | null>(null);
  const resolver = useRef<((answer: boolean) => void) | null>(null);

  const confirm = useCallback((next: ConfirmStepOptions) => new Promise<boolean>(resolve => {
    resolver.current?.(false);
    resolver.current = resolve;
    setOptions(next);
  }), []);

  const answer = (value: boolean) => {
    resolver.current?.(value);
    resolver.current = null;
    setOptions(null);
  };

  const modal = (
    <ConfirmModal
      isOpen={!!options}
      onClose={() => answer(false)}
      onConfirm={() => answer(true)}
      title={options?.title ?? ''}
      message={options?.message ?? ''}
      confirmText={options?.confirmText}
      cancelText={options?.cancelText}
      variant={options?.variant ?? 'warning'}
    />
  );

  return { confirm, modal };
}
